import { LitElement, html, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-checkbox-group.styles';
import { SDS_SIZES } from '../types';
import { SDSCheckbox } from '../sds-checkbox/sds-checkbox';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';

type CheckboxGroupSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;
const updatableCheckboxProps = ['disabled', 'errorMessage', 'readOnly', 'size'] as const;
type UpdatableCheckboxProp = (typeof updatableCheckboxProps)[number];

import '../sds-checkbox/sds-checkbox';
import '../sds-icon/sds-icon';
import '../sds-alert/sds-alert';

// Props Interface
export interface SDSCheckboxGroupProps {
	legend?: string;
	'help-text'?: string;
	'error-message'?: string;
	readonly?: boolean;
	disabled?: boolean;
	size?: CheckboxGroupSize;
}

@guardedCustomElement('sds-checkbox-group')
export class SDSCheckboxGroup extends SDSEventDispatcherMixin(LitElement) {
	// Styling
	static override styles = [styles];

	// Props
	@property({ type: String, reflect: true }) legend?: string;
	@property({ type: String, attribute: 'help-text', reflect: true }) helpText?: string;
	@property({ type: String, attribute: 'error-message', reflect: true }) errorMessage?: string;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: boolean = false;
	@property({ type: Boolean, reflect: true }) disabled = false;
	@property({ type: String, reflect: true }) size: CheckboxGroupSize = 'md';

	@state() private _selectedValues: string[] = [];
	@query('slot') private _slotElement!: HTMLSlotElement;

	private readonly _handleCheckboxChange = (event: CustomEvent): void => {
		const checkbox = event.target as HTMLInputElement;
		const value = checkbox.value;
		const isChecked = checkbox.checked;

		this._selectedValues = isChecked ? [...this._selectedValues, value] : this._selectedValues.filter((v) => v !== value);

		this.dispatchSheriffEvent('sds-update');
	};

	private _handleSlotChange = () => {
		this._updateChildProps([...updatableCheckboxProps]);
	};

	private _getChildCheckboxes(): SDSCheckbox[] {
		return this._slotElement?.assignedElements({ flatten: true }).filter((element): element is SDSCheckbox => element.tagName === 'SDS-CHECKBOX') ?? [];
	}

	private _updateChildProps(changedAttributes: Array<UpdatableCheckboxProp>) {
		const checkboxes = this._getChildCheckboxes();

		for (const childCheckbox of checkboxes) {
			for (const attribute of changedAttributes) {
				switch (attribute) {
					case 'readOnly':
						childCheckbox.readOnly = this.readOnly;
						break;
					case 'disabled':
						childCheckbox.parentRequestDisabled = this.disabled;
						break;
					case 'errorMessage':
						childCheckbox.error = Boolean(this.errorMessage);
						break;
					case 'size':
						childCheckbox.size = this.size;
						break;
				}
			}
		}
	}

	override updated(changedProps: Map<string | number | symbol, unknown>) {
		super.updated(changedProps);

		const changedKeys = Array.from(changedProps.keys());
		if (changedKeys.includes('errorMessage') || changedKeys.includes('error-message')) {
			this._updateChildProps(['errorMessage']);
		}
	}

	override attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
		super.attributeChangedCallback(name, _old, value);

		// DOM attribute for readonly has different casing than our types
		if (name === 'readonly') {
			this._updateChildProps(['readOnly']);
			return;
		}

		const property = name as UpdatableCheckboxProp;
		if (updatableCheckboxProps.includes(property)) {
			this._updateChildProps([property]);
		}
	}

	get value(): {} {
		return { selectedValues: this._selectedValues };
	}

	override render() {
		const groupClasses = {
			'sds-checkbox-group': true,
			'sds-checkbox-group--disabled': this.disabled,
			'sds-checkbox-group--error': Boolean(this.errorMessage),
		};

		return html`
			<fieldset class="${classMap(groupClasses)}">
				${this.legend
					? html`<legend class="sds-checkbox-group__legend">${this.readOnly ? html`<sds-icon name="lock-material-filled" size="md"></sds-icon>` : nothing}</sds-icon>${this.legend}</legend>`
					: nothing}
				${this.helpText ? html`<p class="sds-checkbox-group__help-text">${this.helpText}</p>` : nothing}
				<div class="sds-checkbox-group__checkboxes" @change=${this._handleCheckboxChange}>
					<slot @slotchange=${this._handleSlotChange}></slot>
				</div>
				${this.errorMessage ? html`<sds-alert variant="secondary" data-palette="danger"><p slot="message">${this.errorMessage}</p></sds-alert>` : nothing}
			</fieldset>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-checkbox-group': SDSCheckboxGroup;
	}
}
